import { Chain } from '@1delta/chain-registry'
import { multicallRetryUniversal } from '@1delta/providers'
import type { Address } from 'viem'
import { apyToApr } from '../../../utils/parsing'
import { YieldFetcher } from '../../types'

/**
 * USDD sUSDD intrinsic APR — read straight off the per-chain Pot's
 * `dsr()`.
 *
 * USDD 2.0 (docs.usdd.io) is a Maker fork end to end, and sUSDD is a
 * faithful sDAI clone: an ERC-4626 wrapper over a `Pot` whose `chi()`
 * is the share price, `rho()` the last drip, and `dsr()` the USDD
 * Savings Rate — a per-second compounding `ray` (1e27) exactly like
 * Maker's DSR / Sky's SSR. The APR is a pure function of one call, and
 * the pot address is pinned from the share token's own `pot()` getter
 * (verified on-chain 2026-08-04, along with `chi` matching
 * `convertToAssets(1e18)` on both chains).
 *
 * The rate is PER-CHAIN state — Ethereum and BNB run independent
 * Vat/Jug/Pot stacks (independent `chi`: 1.0609 vs 1.0534 at
 * integration) — so each chain is read separately and emitted under a
 * chain-scoped key, Spark-V2-style. Both pots carried the identical
 * `dsr` (4.00 % APY ≙ 3.9219 % nominal APR) at integration, but
 * nothing ties them together.
 *
 * Fallback: USDD's public API
 * (`openapi.usdd.io/api/v1/external/earn-apy`, the endpoint behind
 * app-api.usdd.io's apy-history) reports the same rate as a compounded
 * APY per chain; it backfills any chain whose RPC read failed, via
 * `apyToApr` to stay on the package-wide nominal-APR convention.
 */

/** 1e27 — `dsr()` and `chi()` are Maker `ray`s. */
const RAY = 10n ** 27n

/** Seconds per (non-leap) year — the DSR annualisation convention. */
const YEAR_SECONDS = 31_536_000

const DSR_ABI = [
  {
    name: 'dsr',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
] as const

/** Per-chain USDD Pot, pinned from the live `sUSDD.pot()` on each
 *  chain (Ethereum sUSDD `0xc5d6a7b6…`, BNB sUSDD `0x8ba9da75…`). */
const USDD_POTS: {
  chainId: string
  pot: Address
  apiKey: 'ethApy' | 'bscApy'
}[] = [
  {
    chainId: Chain.ETHEREUM_MAINNET,
    pot: '0xe789578252cc026ffb3413a1104ba223fdeca500',
    apiKey: 'ethApy',
  },
  {
    chainId: Chain.BNB_SMART_CHAIN_MAINNET,
    pot: '0xf0c506e48383c1925c025ec9f4a9e1dd94ff8b18',
    apiKey: 'bscApy',
  },
]

const USDD_EARN_APY_URL = 'https://openapi.usdd.io/api/v1/external/earn-apy'

/**
 * Result key for one chain's sUSDD. Chain-scoped because the two
 * deployments are independent Maker stacks whose `dsr`s merely happen
 * to agree today.
 */
export const susddKey = (chainId: string): string => `USDD::${chainId}::sUSDD`

/**
 * The canonical token-list `assetGroup`, emitted as a second key so the
 * lending join (`intrinsicYields[asset.assetGroup]`) resolves — sUSDD
 * is already Pendle collateral (SY/PT-sUSDD markets on Ethereum).
 * Verified identical on chains 1 and 56 (2026-08-04). Carries the
 * Ethereum rate — 95 % of sUSDD supply — with BNB as the fallback.
 */
export const SUSDD_GROUP_KEY = 'Savings Usdd::sUSDD'

/**
 * Annualise the per-second `ray` into a percentage **APR** — the
 * simple product, not `^ YEAR_SECONDS − 1`: `supplyRate` is a nominal
 * APR everywhere in this package (see the note in
 * [sparkSavings.ts](./sparkSavings.ts)). Excess over 1 taken in
 * integer space first so the double's precision is spent on the rate,
 * not on the leading `1.000000001…`.
 */
export const aprFromDsr = (dsr: bigint): number => {
  if (dsr <= RAY) return 0
  const perSecond = Number(dsr - RAY) / 1e27
  return perSecond * YEAR_SECONDS * 100
}

/** `dsr()` read for one chain's pot. NB: `multicallRetryUniversal`,
 *  never a bare `getEvmClient` — see the note in [frax.ts](./frax.ts). */
const fetchChainDsr = async (
  chainId: string,
  pot: Address,
): Promise<number> => {
  const [dsr] = (await multicallRetryUniversal({
    chain: chainId,
    abi: DSR_ABI,
    calls: [{ address: pot, name: 'dsr', params: [] }],
    allowFailure: false,
  })) as [bigint]
  if (typeof dsr !== 'bigint' || dsr < RAY) {
    throw new Error(`USDD: unreadable dsr() on chain ${chainId}`)
  }
  return aprFromDsr(dsr)
}

/** Compounded-APY-per-chain from USDD's public API, converted to
 *  nominal APR. Only consulted for chains whose RPC read failed. */
const fetchApiAprs = async (): Promise<
  Partial<Record<'ethApy' | 'bscApy', number>>
> => {
  const res = await fetch(USDD_EARN_APY_URL, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  }).then((r) => r.json() as Promise<any>)
  if (res?.code !== 0 || !res?.data) return {}
  const out: Partial<Record<'ethApy' | 'bscApy', number>> = {}
  for (const key of ['ethApy', 'bscApy'] as const) {
    const apy = Number(res.data[key])
    if (Number.isFinite(apy) && apy > 0) out[key] = apyToApr(apy) * 100
  }
  return out
}

export const usddFetcher: YieldFetcher = {
  label: 'USDD_SUSDD',
  fetch: async () => {
    // Per-chain isolation: a dead BNB endpoint must not zero out the
    // ~$220M Ethereum deployment (or vice versa).
    const settled = await Promise.allSettled(
      USDD_POTS.map(({ chainId, pot }) => fetchChainDsr(chainId, pot)),
    )

    const out: Record<string, number> = {}
    const missed: (typeof USDD_POTS)[number][] = []
    USDD_POTS.forEach((p, i) => {
      const r = settled[i]
      if (r.status === 'fulfilled') out[susddKey(p.chainId)] = r.value
      else missed.push(p)
    })

    if (missed.length > 0) {
      try {
        const api = await fetchApiAprs()
        for (const p of missed) {
          const apr = api[p.apiKey]
          if (apr !== undefined) out[susddKey(p.chainId)] = apr
        }
      } catch {
        // API is the fallback leg — RPC results above stand on their own.
      }
    }

    if (Object.keys(out).length === 0) {
      throw new Error('USDD: no chain returned a readable savings rate')
    }

    const group =
      out[susddKey(Chain.ETHEREUM_MAINNET)] ??
      out[susddKey(Chain.BNB_SMART_CHAIN_MAINNET)]
    if (group !== undefined) out[SUSDD_GROUP_KEY] = group
    return out
  },
}
